/* ══════════════════════════════════════════════════════════════════════
   REVIEW DRILL-DOWN — opened by the Pending / Completed counts.

   One modal, four scopes. The scope dropdown swaps which endpoint is
   called and which columns render; everything else (search, paging,
   selection, the status badge) is shared. Pending and Completed differ
   only in the `status` array sent in the body — see PAG_DRILLDOWN_STATUS.

   Server-side everything: search, paging and sorting are sent to the API
   rather than applied to the page of rows in hand, since the response
   only ever contains the current page.
   ══════════════════════════════════════════════════════════════════════ */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Check, FileDown, Loader2, Search, X } from "lucide-react";
import { useToastStore } from "../../../../stores/toastStore";
import { DataTable, type Column } from "../../../components/DataTable";
import { Dialog, DialogContent, DialogTitle } from "../../../components/ui/dialog";
import { Dropdown } from "../../../components/Dropdown";
import { TABLE_PAGE_SIZE_OPTIONS, safeFormatDate } from "../../../../utils/formatters";
import {
  PAG_DRILLDOWN_STATUS,
  changePagGroupReviewStatus,
  changePagReviewStatus,
  fetchPagGroupDrilldown,
  fetchPagRoleDrilldown,
  fetchPagPreviousCycleDrilldown,
  fetchPagPreviousCycleUserDrilldown,
  fetchPagUserDrilldown,
  type PagReviewAction,
  type PagReviewTargetType,
  type PagDrilldownRequest,
  type PagDrilldownScope,
  type PagDrilldownStatus,
  type PagGroupDrilldownRow,
  type PagRoleDrilldownRow,
  type PagUserDrilldownRow,
} from "../../../../api/frameworksApi";
import type { PagCycle } from "../data/pagTypes";

/* ── Scope options ──────────────────────────────────────────────────── */

const SCOPE_OPTIONS: { value: PagDrilldownScope; label: string }[] = [
  { value: "groups", label: "Groups" },
  { value: "roles", label: "Roles" },
  { value: "users", label: "Users" },
];

type AnyRow = PagUserDrilldownRow | PagGroupDrilldownRow | PagRoleDrilldownRow;

/* ── Shared cell renderers ──────────────────────────────────────────── */

const TEXT = "text-[13px] font-normal leading-5 text-[#334155] dark:text-white/80";
const TEXT_STRONG = "text-[13px] font-semibold leading-5 text-[#1e293b] dark:text-white";
/* Identifiers here (group ids, ARNs, policy names) have no natural break
   points, so wrapping shreds them mid-token — "AGPAVMELC / XLZDD3ITCR /
   DB". Cells stay on one line and the table scrolls sideways instead.
   Only this container scrolls; the page never does. */
/* Truncation is done with DataTable's own `ellipsis` + `tooltip` column
   options (see the col() helper), not CSS here: DataTable nests cell
   content inside a flex wrapper, so text-overflow set on the <td> never
   reaches the text node. */
const CELL = "align-middle!";

/* ── Column widths ──────────────────────────────────────────────────────
   DataTable switches to `table-fixed` + <colgroup> as soon as ANY column
   declares a width — and the selection column does. Columns without one
   then split the leftover space evenly, which is what made "User Last
   Logged On" collide with its neighbours. So every column states its own
   width, and the table's min-width is their sum. */
const W = {
  /* 60, not 44. DataTable pads body cells `px-5` — 40px of the column is
     padding before any content, so a 44px column left the 16px box 4px to
     sit in and `overflow-hidden` clipped the rest. 60 leaves 20px, so the
     box renders whole. Keep PIN_NAME_LEFT in step with this. */
  select: 60,
  groupName: 160,
  groupId: 215,
  username: 175,
  userId: 215,
  createdOn: 140,
  lastLogged: 170,
  attachedPolicy: 210,
  inlinePolicy: 160,
  severity: 145,
  status: 120,
  roleName: 250,
  policyName: 225,
  policyArn: 330,
  mfaEnabled: 120,
  mfaSerial: 215,
  accessLogin: 210,
  policyType: 140,
  access: 125,
  passwordLastUsed: 175,
  count: 130,
} as const;

const px = (n: number) => `${n}px`;
const sumWidths = (cols: { width?: string }[]) =>
  `${cols.reduce((t, c) => t + (parseFloat(c.width ?? "0") || 0), 0)}px`;

/* ── Pinned columns ─────────────────────────────────────────────────────
   The selection box and the name column stay put while the rest scroll.
   Sticky cells need their own opaque background (table rows here are
   transparent) and a z-index above the scrolling cells. The header is
   already `sticky top-0 z-20`, so its pinned cells must outrank that to
   survive both axes at once.

   The seam is drawn with box-shadow rather than a border: the table sets
   `border-collapse: collapse`, under which borders on a sticky cell do not
   travel with it. */
/* These must repaint the EXACT surface they cover, or the frozen columns
   read as a separate panel bolted to the side of the table.

   The header is the subtle one. DataTable stacks two backgrounds there:
   `bg-[#eef2f7] dark:bg-[#1b1c2e]` on <thead>, then
   `bg-[#eef2f7] dark:bg-white/[0.05]` on the header <tr>. In light both
   layers are the same colour, so copying #eef2f7 matched. In dark the
   row's 5% white sits ON TOP of #1b1c2e, and a pinned cell painted a solid
   #1b1c2e came out darker than every header cell beside it. #262738 is
   that blend resolved, so the seam disappears.

   Body rows carry no background of their own — they show the dialog's
   `bg-background` through — so the pinned cells claim the same token. */
const PIN_HEAD = "sticky z-30 bg-[#eef2f7] dark:bg-[#262738]";
const PIN_CELL = "sticky z-10 bg-background";

/* The frozen edge is drawn as a hairline that matches DataTable's own
   column separator exactly, so a pinned column is indistinguishable from
   any other until it actually overlaps something.

   It used to add an 8px gradient shade hanging off the right edge
   (slate-900/15, black/60 in dark). That shade was permanent rather than
   scroll-dependent, and it was what made the first two columns look like
   a floating panel with the rest of the grid sliding under them.

   Kept as a pseudo-element rather than a border: the table sets
   `border-collapse: collapse`, under which a border on a sticky cell does
   not travel with it. The sticky cell is its own positioning context, so
   `absolute` anchors correctly. */
const PIN_EDGE =
  "after:pointer-events-none after:absolute after:inset-y-0 after:-right-px after:w-px " +
  "after:bg-[#e2e8f0] dark:after:bg-white/[0.06]";

/* Written out in full because Tailwind scans source statically: a computed
   `left-[${W.select}px]` would never be generated. Keep in step with
   W.select above — the name column starts where the checkbox ends. */
const PIN_SELECT_LEFT = "left-0";
const PIN_NAME_LEFT = "left-[60px]";

/** Backend sends "N/A" strings and 0 epochs for absent values; both mean
 *  "nothing here" and should read the same way. */
const isBlank = (v: unknown) =>
  v === null || v === undefined || v === "" || v === "N/A" || v === 0 || v === "0";

function Plain({ value }: { value: unknown }) {
  if (isBlank(value)) return <span className="text-[13px] text-[#94A3B8] dark:text-white/40">N/A</span>;
  return <span className={TEXT}>{String(value)}</span>;
}

/** Epoch seconds (users/roles) or a pre-formatted string (groups). */
function DateCell({ value }: { value: number | string | null | undefined }) {
  if (isBlank(value)) return <Plain value={null} />;
  if (typeof value === "number") {
    return <span className={`whitespace-nowrap tabular-nums ${TEXT}`}>{safeFormatDate(value * 1000)}</span>;
  }
  return <span className={`whitespace-nowrap tabular-nums ${TEXT}`}>{String(value)}</span>;
}

/* ── Pills ──────────────────────────────────────────────────────────────
   Same shape and the same tint-on-transparent tones as the Criticality
   column in the Details table behind this modal, so a severity reads the
   same way wherever it appears.

   Width flexes rather than fixing at the 88px that column uses:
   "Uncategorized" is three times the length of "High". */
/* min-w, not w: every pill is the same 88px so a column of them reads as
   one stack rather than a ragged edge -- but "Uncategorized" is longer
   than that on its own and grows instead of being clipped. 88 matches the
   Criticality column in the table behind, and the 145px severity column
   has room for either. */
const BADGE =
  "inline-flex min-w-[88px] items-center justify-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-bold leading-none";

const NEUTRAL_TONE = "bg-slate-500/20 text-slate-400";

/** Whatever case the API sent, rendered as Title case. Values arrive as
 *  "High" today and "HIGH" would shout, which the house rule forbids -- and
 *  a pill is exactly where an unexpected ALL CAPS value would be loudest. */
const titleCase = (v: string) =>
  v.charAt(0).toUpperCase() + v.slice(1).toLowerCase();

const SEVERITY_TONE: Record<string, string> = {
  HIGH: "bg-orange-500/20 text-orange-400",
  MEDIUM: "bg-yellow-500/20 text-yellow-400",
  LOW: "bg-blue-500/20 text-blue-400",
  UNCATEGORIZED: NEUTRAL_TONE,
};

function SeverityBadge({ value }: { value: string | null | undefined }) {
  if (isBlank(value)) return <Plain value={null} />;
  const raw = String(value).trim();
  return (
    <span className={`${BADGE} ${SEVERITY_TONE[raw.toUpperCase()] ?? NEUTRAL_TONE}`}>
      {titleCase(raw)}
    </span>
  );
}

const STATUS_TONE: Record<string, string> = {
  pending: "bg-amber-500/20 text-amber-400",
  continue: "bg-emerald-500/20 text-emerald-400",
  remove: "bg-red-500/20 text-red-400",
};

/** "Enabled & 25/04/2025", or "Disabled" when there is nothing to date.
 *  One column rather than two: the flag on its own never answered the
 *  question the reviewer is actually asking, which is when it was last
 *  used. */
function AccessCell({ on, at }: { on: boolean; at?: number | null }) {
  if (!on) return <span className={TEXT}>Disabled</span>;
  const when = at ? safeFormatDate(at * 1000) : null;
  return (
    <span className={`whitespace-nowrap ${TEXT}`}>
      {when ? `Enabled & ${when}` : "Enabled"}
    </span>
  );
}

function StatusBadge({ value }: { value: string }) {
  if (isBlank(value)) return <Plain value={null} />;
  const raw = String(value).trim();
  return (
    <span className={`${BADGE} ${STATUS_TONE[raw.toLowerCase()] ?? NEUTRAL_TONE}`}>
      {titleCase(raw)}
    </span>
  );
}

/* ── Export ─────────────────────────────────────────────────────────── */

const csvCell = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;

/** CSV of the rows on screen, using the live column set so the export
 *  always matches what the scope is actually showing. */
function exportRows(rows: AnyRow[], columns: Column<AnyRow>[], name: string) {
  const cols = columns.filter((c) => c.key !== "__select");
  const head = cols.map((c) => csvCell(c.header)).join(",");
  const body = rows.map((r) =>
    cols
      .map((c) => csvCell((r as unknown as Record<string, unknown>)[String(c.key)]))
      .join(","),
  );
  const url = URL.createObjectURL(
    new Blob([[head, ...body].join("\r\n")], { type: "text/csv;charset=utf-8;" }),
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = `${name.replace(/[^\w-]+/g, "-").toLowerCase()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/* ── Selection checkbox ─────────────────────────────────────────────── */

/* The box is drawn here rather than left to the browser. A native checkbox
   styled only with `accent-indigo-600` renders through the UA's own dark
   colour-scheme, and the checked fill came out near-invisible against the
   dark table — the tick read as an empty box, so a selected row looked
   unselected. Painting the states explicitly makes both themes certain.

   The input stays in the DOM (sr-only) rather than being replaced by a
   div, so it keeps its focus behaviour, keyboard toggle and label
   semantics for free. */
function CheckCell({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    /* `shrink-0` and `leading-none` are load-bearing, not tidying.

       DataTable wraps every BODY cell in `flex min-w-0 overflow-hidden`,
       but renders header cells through a plain block wrapper. That made
       the box a flex item in the rows and not in the header — and in a
       44px column with cell padding there is under 16px of free width, so
       the row copies were squashed to a stubby rectangle and clipped
       while the header's stayed a clean square. Refusing to shrink is
       what makes the two identical. */
    <label className="inline-flex shrink-0 cursor-pointer items-center leading-none">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        onClick={(e) => e.stopPropagation()}
        aria-label={label}
        className="peer sr-only"
      />
      {/* 1.5px at #64748B, not 1px at #94A3B8. The lighter hairline all but
          vanished against the white row, so an empty box read as no
          control at all rather than as an unticked one. */}
      <span
        className={`flex size-4 shrink-0 items-center justify-center rounded-[3px] border-[1.5px] transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-indigo-500/40 ${
          checked
            ? "border-indigo-600 bg-indigo-600 dark:border-indigo-400 dark:bg-indigo-400"
            : "border-[#64748B] bg-white hover:border-indigo-500 dark:border-white/55 dark:bg-transparent dark:hover:border-indigo-400"
        }`}
      >
        {checked && (
          <Check
            className="size-3 text-white dark:text-[#131320]"
            strokeWidth={3.5}
            aria-hidden
          />
        )}
      </span>
    </label>
  );
}

/* ── Main ───────────────────────────────────────────────────────────── */

export function PagDrilldownModal({
  open,
  onOpenChange,
  accountId,
  accountName,
  appCode,
  status,
  cycle = "current",
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  accountId: string;
  accountName: string;
  appCode: string;
  status: PagDrilldownStatus;
  /** "previous" is closed history: no selection, no actions, export only. */
  cycle?: PagCycle;
}) {
  const isPrevious = cycle === "previous";

  /* Read-only: rows are shown, not acted on -- no checkboxes, no
     Remove/Continue, export in their place.

     Two ways in. A previous cycle is closed history. And a COMPLETED
     drill-down is precisely the list of rows already continued or
     removed, so offering to continue or remove them again offers to redo
     a decision that has been made.

     Kept separate from isPrevious rather than folded into it: that flag
     also picks the endpoint, the default scope and the request body, and
     none of those change because a count was Completed rather than
     Pending. This one governs the chrome only. */
  const readOnly = isPrevious || status === "completed";

  /* Which scopes actually have an endpoint. The previous cycle covers
     Roles and Users, with Groups still to come. Anything without a source
     shows an explanatory panel rather than an empty table that reads as
     "no data". */
  const scopeHasEndpoint = (s: PagDrilldownScope) =>
    isPrevious ? s === "roles" || s === "users" : true;

  const scopeLabel = (s: PagDrilldownScope) =>
    SCOPE_OPTIONS.find((o) => o.value === s)?.label ?? s;
  const showSuccess = useToastStore((s) => s.showSuccess);
  const showError = useToastStore((s) => s.showError);
  const [scope, setScope] = useState<PagDrilldownScope>(
    cycle === "previous" ? "roles" : "groups",
  );
  const [keyword, setKeyword] = useState("");
  /** Debounced copy of `keyword` — the search hits the server, so it must
   *  not fire once per keystroke. */
  const [appliedKeyword, setAppliedKeyword] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rows, setRows] = useState<AnyRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /* Keyed by row key, holding the ROW — a selection survives paging, and by
     then the row is no longer in `rows`, but the action still needs its id
     (and, for groups, its groupType). */
  const [selected, setSelected] = useState<Map<string, AnyRow>>(new Map());
  const [acting, setActing] = useState<PagReviewAction | null>(null);

  /* Reset everything when the modal is opened for a different account or a
     different count, so one drill-down never shows another's state. */
  useEffect(() => {
    if (!open) return;
    setScope(isPrevious ? "roles" : "groups");
    setKeyword("");
    setAppliedKeyword("");
    setPage(0);
    setSelected(new Map());
    setRows([]);
    setTotal(0);
    setError(null);
  }, [open, accountId, status]);

  useEffect(() => {
    const t = setTimeout(() => {
      setAppliedKeyword(keyword.trim());
      setPage(0);
    }, 350);
    return () => clearTimeout(t);
  }, [keyword]);

  /* `id` alone is NOT unique per row. It is the POLICY id, and one policy
     is attached to many identities — AdministratorAccess comes back as id
     14 on every role that holds it. Keying on it alone made those rows
     share one key, so ticking one ticked every other row carrying the
     same policy.

     The row's absolute position is what actually identifies it, so that
     leads. `id` is kept alongside purely to make the key readable when
     debugging a selection. */
  const rowKeyOf = useCallback(
    (r: AnyRow, i: number) =>
      `${scope}:${page * pageSize + i}:${(r as { id?: number }).id ?? "n"}`,
    [scope, page, pageSize],
  );

  /* Declared above `load` on purpose: `load` reads it, and a const
     referenced before its initialiser throws at runtime. */
  const hasEndpoint = scopeHasEndpoint(scope);

  const load = useCallback(() => {
    if (!open || !hasEndpoint) {
      if (!hasEndpoint) {
        setRows([]);
        setTotal(0);
        setError(null);
      }
      return;
    }

    /* The previous-cycle endpoint takes accountId only — it is not filtered
       by review status, so `status` is left off the body rather than sent
       empty. The current cycle still needs it: Pending and Completed are
       the same endpoint with different status arrays. */
    const body: PagDrilldownRequest = {
      accountId,
      appCode,
      keyword: appliedKeyword,
      sortByColumn: "",
      sortingOrder: "",
      ...(isPrevious ? {} : { status: PAG_DRILLDOWN_STATUS[status] }),
    };

    const call = isPrevious
      ? scope === "users"
        ? fetchPagPreviousCycleUserDrilldown
        : fetchPagPreviousCycleDrilldown
      : scope === "users"
        ? fetchPagUserDrilldown
        : scope === "roles"
          ? fetchPagRoleDrilldown
          : fetchPagGroupDrilldown;

    let cancelled = false;
    setLoading(true);
    setError(null);
    (call as (
      b: PagDrilldownRequest,
      p: number,
      s: number,
    ) => Promise<{ data: AnyRow[]; totalElements?: number; count?: number }>)(
      body,
      page,
      pageSize,
    )
      .then((res) => {
        if (cancelled) return;
        setRows(res?.data ?? []);
        /* The previous-cycle USER endpoint reports its total as `count`;
           every other drill-down uses `totalElements`. Reading both means
           the pager works whichever envelope arrives, instead of showing
           "0 records" under a full page of rows. */
        setTotal(res?.totalElements ?? res?.count ?? 0);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setRows([]);
        setTotal(0);
        setError(e instanceof Error ? e.message : "Could not load these records");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, scope, hasEndpoint, accountId, appCode, status, appliedKeyword, page, pageSize]);

  useEffect(() => {
    const cleanup = load();
    return cleanup;
  }, [load]);

  /* ── Columns ──────────────────────────────────────────────────────── */

  const pageKeys = useMemo(() => rows.map(rowKeyOf), [rows, rowKeyOf]);
  const allOnPageSelected = pageKeys.length > 0 && pageKeys.every((k) => selected.has(k));

  const toggleAllOnPage = () =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (allOnPageSelected) pageKeys.forEach((k) => next.delete(k));
      else rows.forEach((r, i) => next.set(rowKeyOf(r, i), r));
      return next;
    });

  const toggleOne = (key: string, row: AnyRow) =>
    setSelected((prev) => {
      const next = new Map(prev);
      next.has(key) ? next.delete(key) : next.set(key, row);
      return next;
    });

  const selectColumn: Column<AnyRow> = {
    key: "__select",
    width: px(W.select),
    headerClassName: `${PIN_HEAD} ${PIN_SELECT_LEFT}`,
    cellClassName: `${CELL} ${PIN_CELL} ${PIN_SELECT_LEFT}`,
    header: (
      <CheckCell
        checked={allOnPageSelected}
        onChange={toggleAllOnPage}
        label="Select all rows on this page"
      />
    ),
    render: (r, i) => {
      const k = rowKeyOf(r, i);
      return (
        <CheckCell
          checked={selected.has(k)}
          onChange={() => toggleOne(k, r)}
          label="Select row"
        />
      );
    },
  };

  const col = (
    key: string,
    header: string,
    width: number,
    render: (r: AnyRow) => React.ReactNode,
    opts: Partial<Column<AnyRow>> = {},
  ): Column<AnyRow> => ({
    key,
    header,
    width: px(width),
    align: "left",
    headerAlign: "left",
    cellClassName: CELL,
    /* Single-line clamp: values here are identifiers with no sensible wrap
       point, and every column has a fixed width. DataTable falls back to
       the cell's own text for the tooltip when a column supplies none, so
       the full string stays reachable on hover. */
    ellipsis: 1,
    /* Header text is nowrap in DataTable, so a long label would be clipped
       rather than wrapped — the tooltip keeps it reachable. */
    headerTooltip: header,
    render,
    ...opts,
  });

  const nameLeft = readOnly ? PIN_SELECT_LEFT : PIN_NAME_LEFT;

  /** The pinned name column: sits beside the selection box and carries the
   *  seam shadow, since it is the last column that stays put. */
  const nameCol = (
    key: string,
    header: string,
    width: number,
    label: (r: AnyRow) => string,
  ): Column<AnyRow> =>
    col(key, header, width, (r) => (
      <span className="text-[13px] font-semibold text-indigo-600 dark:text-indigo-400">
        {label(r)}
      </span>
    ), {
      /* Sits where the checkbox ends — but a closed cycle has no checkbox,
         so it must pin flush left or it leaves a 44px hole. */
      headerClassName: `${PIN_HEAD} ${PIN_EDGE} ${nameLeft}`,
      cellClassName: `${CELL} ${PIN_CELL} ${PIN_EDGE} ${nameLeft}`,
      tooltip: (r) => label(r),
    });

  const columns: Column<AnyRow>[] = useMemo(() => {
    const g = (r: AnyRow) => r as PagGroupDrilldownRow;
    const u = (r: AnyRow) => r as PagUserDrilldownRow;
    const ro = (r: AnyRow) => r as PagRoleDrilldownRow;

    /* A closed cycle is a record, not a worklist: nothing here can be
       actioned, so the selection column would be a dead control.

       `isPrevious` alone used to send EVERY previous-cycle scope down this
       branch, which was right while Roles was the only one it served — its
       endpoint is named "...Role..." but returns the group row shape. The
       previous-cycle Users endpoint returns the USER shape, so it has to
       fall through to the user columns below or every field reads N/A. */
    if ((isPrevious && scope !== "users") || scope === "groups") {
      return [
        ...(readOnly ? [] : [selectColumn]),
        nameCol("groupName", "Group Name", W.groupName, (r) => g(r).groupName),
        col("groupId", "Group ID", W.groupId, (r) => <Plain value={g(r).groupId} />),
        col("userName", "Username", W.username, (r) => <Plain value={g(r).userName} />),
        col("userId", "User ID", W.userId, (r) => <Plain value={g(r).userId} />),
        col("createdDate", "User Created On", W.createdOn, (r) => (
          <DateCell value={g(r).createdDate} />
        )),
        col("passwordLastUsed", "User Last Logged On", W.lastLogged, (r) => (
          <DateCell value={g(r).passwordLastUsed} />
        )),
        col("policyName", "Attached Policy", W.attachedPolicy, (r) => (
          <Plain value={g(r).policyName} />
        )),
        col("inlinePolicy", "Inline Policy", W.inlinePolicy, (r) => (
          <Plain value={g(r).inlinePolicy} />
        )),
        col("severity", "Policy Severity", W.severity, (r) => (
          <SeverityBadge value={g(r).severity} />
        )),
        col("status", "Status", W.status, (r) => <StatusBadge value={g(r).status} />),
      ];
    }

    if (scope === "roles") {
      return [
        selectColumn,
        nameCol("roleName", "Role Name", W.roleName, (r) => ro(r).roleName),
        col("policyName", "Policy Name", W.policyName, (r) => <Plain value={ro(r).policyName} />, {
          width: undefined,
          tooltip: (r) => ro(r).policyName,
        }),
        /* The ONE column in this scope with no fixed width, on purpose.
           Roles declares far fewer columns than users or groups, so its
           widths add up to well under the modal — and `table-fixed` left
           that slack sitting at the right-hand end as an empty strip past
           Status. An unsized column absorbs the remainder instead, so the
           grid always ends flush at any modal width.

           Policy Name carries it now that ARN is gone: it holds the
           longest remaining values, and every other column here keeps its
           width, so exactly one column flexes and the leftover cannot
           split unevenly. */
        col("policyType", "Policy Type", W.policyType, (r) => <Plain value={ro(r).policyType} />),
        col("severity", "Policy Severity", W.severity, (r) => (
          <SeverityBadge value={ro(r).severity} />
        )),
        col("status", "Status", W.status, (r) => <StatusBadge value={ro(r).status} />),
      ];
    }

    // users — both cycles, since the row shape is identical. The checkbox
    // follows readOnly: only a list that can still be acted on gets one.
    return [
      ...(readOnly ? [] : [selectColumn]),
      nameCol("userName", "Username", W.username, (r) => u(r).userName),
      col("userId", "User ID", W.userId, (r) => <Plain value={u(r).userId} />),
      /* MFA is reported off the device list rather than a flag: the row has
         no mfaEnabled of its own, and "has a device registered" is the same
         fact. The first device carries the serial and the date. */
      col("mfaEnabled", "MFA Enabled", W.mfaEnabled, (r) => (
        <span className={TEXT}>{u(r).mfaDevices?.length ? "Yes" : "No"}</span>
      )),
      col("mfaSerial", "MFA Device Sl. No", W.mfaSerial, (r) => (
        <Plain value={u(r).mfaDevices?.[0]?.serialNumber} />
      )),
      col("mfaEnabledOn", "MFA Enabled On", W.createdOn, (r) => (
        <DateCell value={u(r).mfaDevices?.[0]?.enabledDate} />
      )),
      col("consoleAccess", "Console Access & Last Login", W.accessLogin, (r) => (
        <AccessCell on={u(r).consoleAccess} at={u(r).passwordLastUsed} />
      )),
      col("cliAccess", "CLI Access & Last Login", W.accessLogin, (r) => (
        <AccessCell on={u(r).cliAccess} at={u(r).accessKeys?.[0]?.lastUsed} />
      )),
      col("policyType", "Policy Type", W.policyType, (r) => <Plain value={u(r).policyType} />),
      /* Sized, NOT flexed like the roles scope. Roles declares few enough
         columns to leave slack for an unsized one to absorb; users declares
         eleven and already overflows the modal. An unsized column there made
         table-fixed shrink every sized column to fit instead, which squeezed
         Policy Severity under its own nowrap header until it collided with
         Status. The table scrolls sideways here, so the widths must hold. */
      col("policyName", "Policy Name", W.policyName, (r) => <Plain value={u(r).policyName} />, {
        tooltip: (r) => u(r).policyName,
      }),
      col("severity", "Policy Severity", W.severity, (r) => (
        <SeverityBadge value={u(r).severity} />
      )),
      col("status", "Status", W.status, (r) => <StatusBadge value={u(r).status} />),
    ];
    // selectColumn closes over selection state, so it must be re-created
    // whenever that changes or the header checkbox goes stale.
  }, [scope, selected, pageKeys, allOnPageSelected]);

  const selectedCount = selected.size;

  /**
   * Applies Continue or Remove to the current selection.
   *
   * Groups go to their own endpoint as an ARRAY: a single selection can mix
   * membership rows with policy rows, and each id has to travel under the
   * type naming the table it belongs to. That type is per-row (`groupType`),
   * so the ids are bucketed by it rather than sent under one blanket type.
   */
  const runAction = (status: PagReviewAction) => {
    const picked = [...selected.values()];
    if (!picked.length || acting) return;

    setActing(status);

    const request =
      scope === "groups"
        ? (() => {
            const byType = new Map<PagReviewTargetType, number[]>();
            picked.forEach((r) => {
              const type =
                ((r as PagGroupDrilldownRow).groupType as PagReviewTargetType) ||
                "groupPolicy";
              const ids = byType.get(type) ?? [];
              ids.push(r.id);
              byType.set(type, ids);
            });
            return changePagGroupReviewStatus(
              [...byType.entries()].map(([type, ids]) => ({ ids, type, status })),
            );
          })()
        : changePagReviewStatus({
            ids: picked.map((r) => r.id),
            type: scope === "users" ? "users" : "roles",
            status,
          });

    request
      .then(() => {
        showSuccess(
          status === "Continue" ? "Access continued" : "Access removed",
          `${picked.length} ${picked.length === 1 ? "record" : "records"} updated.`,
        );
        setSelected(new Map());
        // Re-read: the rows just actioned leave the Pending list entirely.
        load();
      })
      .catch((e: unknown) =>
        showError(
          `Could not ${status.toLowerCase()}`,
          e instanceof Error ? e.message : "The change was not saved.",
        ),
      )
      .finally(() => setActing(null));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Compact drill-down, not a full-page table. Height is capped at
          MODAL_MAX_H and never grows with the row count — extra rows go to
          the table's own scroll area or to the next page. Width is generous
          because the columns are wide, but extra columns scroll rather than
          widening the dialog. */}
      <DialogContent
        showCloseButton={false}
        className="flex max-h-[70vh] w-[calc(100vw-4rem)] max-w-[1440px] flex-col gap-0 overflow-hidden p-0 sm:max-w-[1440px]"
      >
        {/* ── Header ─────────────────────────────────────────────────── */}
        <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-b border-border px-5 py-2.5">
          <DialogTitle className={TEXT_STRONG}>{accountName}</DialogTitle>

          <Dropdown
            value={scope}
            onChange={(v) => {
              setScope(v as PagDrilldownScope);
              setPage(0);
              setSelected(new Map());
            }}
            options={SCOPE_OPTIONS}
            size="sm"
            variant="pill"
            searchable
            className="min-w-[150px]"
          />

          <div className="ml-auto flex flex-wrap items-center gap-2">
            <div className="relative w-[190px]">
              <input
                type="text"
                value={keyword}
                maxLength={120}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="Search"
                disabled={!hasEndpoint}
                className="h-8 w-full rounded-lg border border-border bg-background pl-3 pr-8 text-xs text-foreground outline-none transition-colors focus:border-indigo-500 disabled:opacity-50"
              />
              {keyword ? (
                <button
                  onClick={() => setKeyword("")}
                  title="Clear search"
                  aria-label="Clear search"
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
                >
                  <X className="size-3.5" />
                </button>
              ) : (
                <Search className="pointer-events-none absolute right-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
              )}
            </div>

            {readOnly ? (
              <button
                onClick={() => exportRows(rows, columns, `${accountName}-previous-cycle`)}
                disabled={!rows.length}
                className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-border px-3.5 text-sm font-semibold text-foreground transition-colors hover:bg-muted disabled:cursor-default disabled:opacity-40"
              >
                <FileDown className="size-3.5" />
                Generate Report
              </button>
            ) : (
            <>
            <button
              onClick={() => runAction("Remove")}
              disabled={!selectedCount || !!acting}
              title={selectedCount ? `Remove ${selectedCount} selected` : "Select rows first"}
              className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-[#F87171] px-4 text-sm font-semibold text-white transition-colors hover:bg-[#EF4444] disabled:cursor-default disabled:opacity-40"
            >
              {acting === "Remove" && <Loader2 className="size-3.5 animate-spin" />}
              Remove
            </button>
            <button
              onClick={() => runAction("Continue")}
              disabled={!selectedCount || !!acting}
              title={selectedCount ? `Continue ${selectedCount} selected` : "Select rows first"}
              className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-[#5EEAD4] px-4 text-sm font-semibold text-[#0F766E] transition-colors hover:bg-[#2DD4BF] disabled:cursor-default disabled:opacity-40"
            >
              {acting === "Continue" && <Loader2 className="size-3.5 animate-spin" />}
              Continue
            </button>
            </>
            )}

            {/* Part of the header row rather than floating off the modal's
                corner, so it reads as a control and cannot clip the border. */}
            <button
              onClick={() => onOpenChange(false)}
              aria-label="Close"
              title="Close"
              className="ml-1 flex size-8 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/50"
            >
              <X className="size-4" />
            </button>
          </div>
        </div>

        {/* ── Body ─────────────────────────────────────────────────────
            No overflow here: DataTable owns the single scroll container, so
            header and body move together and there is only ever one
            horizontal scrollbar. min-h-0 lets it shrink inside the capped
            dialog instead of pushing it taller. */}
        <div className="flex min-h-0 flex-1 flex-col px-5 py-3">
          {!hasEndpoint ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border py-16 text-center">
              <p className="text-[13px] font-semibold text-foreground">
                {scopeLabel(scope)} are not available yet
              </p>
              <p className="mt-1 text-[13px] text-muted-foreground">
                {isPrevious
                  ? "The previous cycle exposes Roles and Users so far. Pick one of those."
                  : "No endpoint exists for this scope. Pick Groups, Roles, or Users."}
              </p>
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={rows}
              rowKey={rowKeyOf}
              loading={loading}
              error={error}
              onRetry={load}
              emptyText={`No ${status} records for this account.`}
              page={page}
              pageSize={pageSize}
              totalItems={total}
              onPageChange={setPage}
              pagerStyle="simple"
              pageSizeOptions={[...TABLE_PAGE_SIZE_OPTIONS]}
              onPageSizeChange={(s) => {
                setPageSize(s);
                setPage(0);
              }}
              itemLabel="records"
              responsive
              compact
              /* Derived from the columns actually rendered, so each scope
                 gets exactly the width its own columns need. */
              minTableWidth={sumWidths(columns)}
              /* Default is 100 chars, which would clip full policy ARNs.
                 The table scrolls sideways, so they can be shown whole. */
              maxCellChars={512}
              /* Caps the scrollable header+body area so ~9 compact rows show
                 before it scrolls. Subtracts the dialog's own header, body
                 padding and pager from the 70vh shell, so the modal height
                 stays constant no matter how many rows come back. */
              maxHeight="calc(70vh - 150px)"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
